<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import CarExtrasCard from '@/components/cards/CarExtrasCard.vue';
import Buttons from '@/components/ui/Buttons.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';

import 'swiper/css/pagination';
import 'swiper/css/navigation';

import '@/assets/styles/swiper-custom.scss';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  description,
  items,
  hasTitle,
  hasDescription,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);

const activeTab = ref(items.value[0]?.id);
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
const isLocked = ref(true);
const onSwiper = (swiper) => {
  isLocked.value = swiper.isLocked;
};

const onLockToggle = (swiper) => {
  isLocked.value = swiper.isLocked;
};
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? `white` : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="car-extras">
        <div v-if="hasTitle || hasDescription" class="car-extras__text">
          <h2
            v-if="hasTitle"
            class="mb-0 car-extras__title h2 text-center"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h2>
          <div
            v-if="hasDescription"
            class="car-extras__description body-3-regular text-center"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="description"
          ></div>
        </div>
        <Tabs
          v-if="hasItems"
          v-model:value="activeTab"
          :scrollable="false"
          class="car-extras__tabs"
        >
          <TabList
            ref="tabList"
            :class="`car-extras__tabs-headers tabs-bg-${backgroundColor}`"
          >
            <Tab
              v-for="(tab, tabIndex) in items"
              :key="tab.id"
              :value="tab.id"
              :class="[
                `car-extras__tabs-title car-extras__tabs-title--${backgroundColor}`,
              ]"
              data-aos="fade-up"
              :data-aos-delay="tabIndex >= 1 ? 100 + 100 * tabIndex : 100"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="car-extras__tabs-content">
            <TabPanel
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              class="car-extras__slider"
            >
              <Swiper
                :slides-per-view="'auto'"
                :space-between="16"
                :modules="[Navigation, Pagination]"
                :navigation="{
                  prevEl: `#${tab.id}Prev`,
                  nextEl: `#${tab.id}Next`,
                }"
                :pagination="{
                  el: `#${tab.id}Pagination`,
                }"
                data-aos="fade-up"
                data-aos-easing="ease-out-cubic"
                @swiper="onSwiper"
                @lock="onLockToggle"
                @unlock="onLockToggle"
              >
                <SwiperSlide v-for="(item, index) in tab.items" :key="index">
                  <CarExtrasCard
                    :title="item?.title"
                    :image="item?.image"
                    :article="item?.article"
                    :url="item?.url"
                  />
                </SwiperSlide>
              </Swiper>
              <div
                v-show="!isLocked"
                :id="`${tab.id}Pagination`"
                data-aos="fade-up"
                data-aos-easing="ease-out-cubic"
                :data-delay="
                  tab.items && tab.items.length > 0
                    ? tab.items.length * 100
                    : 100
                "
                class="swiper-pagination car-extras__slider-pagination"
              ></div>
              <div
                v-show="!isLocked"
                data-aos="fade-up"
                data-aos-easing="ease-out-cubic"
                :data-delay="
                  tab.items && tab.items.length > 0
                    ? tab.items.length * 100
                    : 100
                "
                class="swiper-navigation car-extras__slider-navigation"
              >
                <Button
                  :id="`${tab.id}Prev`"
                  type="button"
                  rounded
                  variant="outlined"
                  class="car-extras__slider-navigation-prev button-prev js-button-car-extras-prev"
                >
                  <CIcon
                    class="button-prev__icon"
                    :name="
                      backgroundColor === 'black'
                        ? 'direction-white'
                        : 'direction-black'
                    "
                    width="16"
                    height="16"
                  />
                </Button>
                <Button
                  :id="`${tab.id}Next`"
                  type="button"
                  rounded
                  variant="outlined"
                  class="car-extras__slider-navigation-next button-next js-button-car-extras-next"
                >
                  <CIcon
                    class="button-next__icon"
                    :name="
                      backgroundColor === 'black'
                        ? 'direction-white'
                        : 'direction-black'
                    "
                    width="16"
                    height="16"
                  />
                </Button>
              </div>
              <Buttons
                v-if="tab.buttons && tab.buttons.length > 0"
                class="car-extras__buttons"
                :buttons="tab.buttons"
                :background-color="data?.background_color"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.car-extras {
  display: flex;
  flex-direction: column;
  gap: 80px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__description {
    @include media-breakpoint-up(sm) {
      @include body-1-regular;
      margin: 0 auto;
      max-width: 80.3%;
    }
    @include media-breakpoint-up(md) {
      max-width: 49.4%;
    }
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-tab-list) {
        justify-content: center;
      }
      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      border-bottom: 1px solid $divider;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
      &--white,
      &--gray {
        color: $on-surface-variant-2;
      }
      &--black {
        color: $on-surface-variant-2;
        &[data-p='active'] {
          color: $white;
        }
      }
    }
  }
  &__slider {
    .swiper {
      height: 100%;
      overflow: visible;
      width: 100%;
    }
    .swiper-slide {
      height: auto;
      max-width: calc(100vw - 48px);
      width: 312px;
      @include media-breakpoint-up(sm) {
        max-width: unset;
        width: 226px;
      }
      @include media-breakpoint-up(md) {
        width: 284px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 284);
      }
    }
  }
  &__buttons {
    margin: 40px auto 0;
    max-width: fit-content;
    @include media-breakpoint-up(sm) {
      margin-top: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-top, 56);
    }
  }
}
</style>
